import React, { useState } from "react";
import {
  Box,
  Drawer,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText as MuiListItemText,
  Collapse,
  Divider,
  Toolbar,
  IconButton,
} from "@mui/material";
import {
  ChevronLeft,
  Person,
  Dashboard,
  ManageAccounts,
  School,
  People,
  MeetingRoom,
  CoPresent,
  EditNote,
  Class,
  EventAvailable,
  AssignmentTurnedIn,
  Palette,
  MenuBook,
  Book,
  ListAlt,
  Description,
  NoteAlt,
  TrendingUp,
  DoorBack,
  Logout,
  ExpandLess,
  ExpandMore,
  AccountTree,
  EmojiPeople,
  VolunteerActivism,
  Groups,
  Mosque,
  Savings,
  AccountBalance,
  DateRange,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";
import { resetReducer, selectCurrentUser } from "../../features/auth/authSlice";
import { useLogoutMutation } from "../../features/auth/authApiSlice";
import { useGetSchoolByIdQuery } from "../../features/school/schoolApiSlice";
import { apiSlice } from "../../app/api/apiSlice";
import intro from "../../assets/intro-AlHasanah-unscreen (1).gif";
import { getRoleKey } from "../../RoleList.mjs";
import appVersion from "../../utils/appVersion";

const drawerWidth = 260;

const ListItemText = (props) => (
  <MuiListItemText
    {...props}
    primaryTypographyProps={{
      fontSize: "1.1rem",
      fontWeight: "bold",
      ...props.primaryTypographyProps,
    }}
  />
);

const Sidebar = ({ mobileOpen, handleDrawerToggle, desktopOpen = true }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const [logout] = useLogoutMutation();
  const currentUser = useSelector(selectCurrentUser);
  const { selectedRole, selectedSchoolUuid } = useSelector(
    (state) => state.auth,
  );

  const hasRole = (roleName) => getRoleKey(selectedRole) === roleName;
  const isCsTabungan = hasRole("TENDIK");

  // Fetch school data only if we explicitly selected a school (e.g. Admin switching views)
  const { data: school } = useGetSchoolByIdQuery(selectedSchoolUuid, {
    skip: !selectedSchoolUuid,
  });

  // Use the fetched school level (If Admin/YayasanPM) OR the logged-in user's school level
  // This prevents stale selectedSchoolId from affecting non-Admin roles (like Wakur)
  const currentSchoolLevel =
    (hasRole("ADMIN") || hasRole("YAYASAN")) && school?.strLevel
      ? school.strLevel
      : currentUser?.schoolLevel;

  const currentSchoolType =
    (hasRole("ADMIN") || hasRole("YAYASAN")) && school?.strType
      ? school.strType
      : currentUser?.schoolType;

  const isMiddleOrHigh =
    currentSchoolLevel === "Middle" || currentSchoolLevel === "High";
  const isReligious = currentSchoolType === "Religious";
  const isEarlyChildhood = currentSchoolLevel === "Early Childhood";
  const isWakurPaud = hasRole("WAKUR") && isEarlyChildhood;
  const isKepsekPaud = hasRole("KEPSEK") && isEarlyChildhood;
  const isPaudMonitorLimited = isWakurPaud || isKepsekPaud;
  const kokurikulerMenuLabel = "Kokurikuler";

  // DEBUG LOGS
  console.log("Sidebar Debug:", {
    role: getRoleKey(selectedRole),
    schoolLevel: currentSchoolLevel,
    isMiddleOrHigh,
    hasWalasRole: hasRole("WALIKELAS"),
    currentUserSchoolLevel: currentUser?.schoolLevel,
    isReligious,
  });

  // State for collapsible menus
  const [openKepegawaian, setOpenKepegawaian] = useState(false);
  const [openKelasWakur, setOpenKelasWakur] = useState(false);
  const [openAbsensi, setOpenAbsensi] = useState(false);
  const [openSikap, setOpenSikap] = useState(false);
  const [openRapor, setOpenRapor] = useState(false);
  const [openMonitorRaport, setOpenMonitorRaport] = useState(false);
  const [openAcuan, setOpenAcuan] = useState(false);
  const [openTabungan, setOpenTabungan] = useState(false);

  const handleLogout = async () => {
    try {
      await logout({ strUserName: currentUser?.strUserName }).unwrap();
      navigate("/", { replace: true });
      setTimeout(() => {
        dispatch(resetReducer());
        dispatch(apiSlice.util.resetApiState());
      }, 100);
    } catch (err) {
      navigate("/", { replace: true });
      setTimeout(() => {
        dispatch(resetReducer());
        dispatch(apiSlice.util.resetApiState());
      }, 100);
    }
  };

  const navigateWithRefresh = (path) => {
    dispatch(
      apiSlice.util.invalidateTags([
        "RekapNilai",
        "RekapNilaiTengahSemester",
        "RekapNilaiWakur",
      ]),
    );

    if (location.pathname === path) {
      navigate(`${path}?refresh=${Date.now()}`, { replace: true });
      return;
    }

    navigate(path);
  };

  // Custom styled ListItemButton
  const StyledListItemButton = ({
    children,
    onClick,
    selected,
    sx = {},
    ...props
  }) => (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      sx={{
        my: 0.5,
        mx: 1.5,
        borderRadius: 2,
        color: "rgba(255, 255, 255, 0.8)",
        "&.Mui-selected": {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.25)",
          },
          "& .MuiListItemIcon-root": {
            color: "#ffffff",
          },
        },
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          color: "#ffffff",
          "& .MuiListItemIcon-root": {
            color: "#ffffff",
          },
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </ListItemButton>
  );

  const StyledListItemIcon = ({ children }) => (
    <ListItemIcon sx={{ color: "rgba(255, 255, 255, 0.6)", minWidth: 40 }}>
      {children}
    </ListItemIcon>
  );

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #5CA8D0 0%, #367FA9 100%)",
        color: "#fff",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          height: { xs: "120px", sm: "130px" },
          bgcolor: "#ffffff", // Solid white background for logo area
          color: "#1565c0", // Dark blue text for logo area
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: { sm: "none" },
            color: "#1565c0",
            zIndex: 10,
          }}
        >
          <ChevronLeft />
        </IconButton>
        <img
          src={intro}
          alt="Logo"
          style={{
            maxHeight: "100%",
            maxWidth: "90%",
            objectFit: "contain",
            padding: "4px",
          }}
        />
      </Toolbar>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)", mb: 1 }} />

      <Box
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          px: 0,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <List
          component="nav"
          sx={{
            display: isCsTabungan ? "none" : "flex",
            flexDirection: "column",
          }}
        >
          <StyledListItemButton
            onClick={() => navigate("/profile")}
            selected={location.pathname === "/profile"}
          >
            <StyledListItemIcon>
              <Person />
            </StyledListItemIcon>
            <ListItemText primary="Profil" />
          </StyledListItemButton>

          <StyledListItemButton
            onClick={() => navigate("/dashboard")}
            selected={location.pathname === "/dashboard"}
          >
            <StyledListItemIcon>
              <Dashboard />
            </StyledListItemIcon>
            <ListItemText primary="Beranda" />
          </StyledListItemButton>

          {hasRole("ADMIN") && (
            <StyledListItemButton
              onClick={() => navigate("/login-activty")}
              selected={location.pathname === "/login-activty"}
            >
              <StyledListItemIcon>
                <ManageAccounts />
              </StyledListItemIcon>
              <ListItemText primary="Aktivitas Login" />
            </StyledListItemButton>
          )}

          {(hasRole("ADMIN") || hasRole("YAYASAN")) && (
            <StyledListItemButton
              onClick={() => navigate("/ortu-login-activity")}
              selected={location.pathname === "/ortu-login-activity"}
            >
              <StyledListItemIcon>
                <ManageAccounts />
              </StyledListItemIcon>
              <ListItemText primary="Aktivitas Login Wali Murid" />
            </StyledListItemButton>
          )}

          {(hasRole("ADMIN") || hasRole("YAYASAN")) && (
            <StyledListItemButton
              onClick={() => navigate("/menu-activity")}
              selected={location.pathname === "/menu-activity"}
            >
              <StyledListItemIcon>
                <ManageAccounts />
              </StyledListItemIcon>
              <ListItemText primary="Aktivitas Menu" />
            </StyledListItemButton>
          )}

          {hasRole("ADMIN") && (
            <StyledListItemButton
              onClick={() => navigate("/schools")}
              selected={location.pathname === "/schools"}
            >
              <StyledListItemIcon>
                <School />
              </StyledListItemIcon>
              <ListItemText primary="Manajemen Sekolah" />
            </StyledListItemButton>
          )}

          {(hasRole("KEPSEK") ||
            hasRole("YAYASAN") ||
            hasRole("WAKUR") ||
            hasRole("TATAUSAHA") ||
            hasRole("ADMIN")) && (
            <StyledListItemButton
              onClick={() => navigate("/create-identitas")}
              selected={location.pathname === "/create-identitas"}
            >
              <StyledListItemIcon>
                <School />
              </StyledListItemIcon>
              <ListItemText primary="Identitas Sekolah" />
            </StyledListItemButton>
          )}

          <StyledListItemButton
            onClick={() => navigate("/strucktur-organisasi")}
            selected={location.pathname === "/strucktur-organisasi"}
          >
            <StyledListItemIcon>
              <AccountTree />
            </StyledListItemIcon>
            <ListItemText primary="Struktur Organisasi" />
          </StyledListItemButton>

          {/* Kepegawaian Dropdown */}
          {!hasRole("GURU") &&
            !hasRole("WALIKELAS") &&
            !hasRole("BENDAHARA") && (
              <>
                <StyledListItemButton
                  onClick={() => setOpenKepegawaian(!openKepegawaian)}
                >
                  <StyledListItemIcon>
                    <People />
                  </StyledListItemIcon>
                  <ListItemText primary="Kepegawaian" />
                  {openKepegawaian ? <ExpandLess /> : <ExpandMore />}
                </StyledListItemButton>
                <Collapse in={openKepegawaian} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                  >
                    {!isWakurPaud && (
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate("/kelas")}
                        selected={location.pathname === "/kelas"}
                      >
                        <StyledListItemIcon>
                          <Groups />
                        </StyledListItemIcon>
                        <ListItemText primary="Kelas" />
                      </StyledListItemButton>
                    )}
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/guru")}
                      selected={location.pathname === "/guru"}
                    >
                      <StyledListItemIcon>
                        <CoPresent />
                      </StyledListItemIcon>
                      <ListItemText primary="Guru" />
                    </StyledListItemButton>
                    {/* Menu Tendik sementara di-hide untuk role TU */}
                    {hasRole("ADMIN") && (
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate("/akun-tendik")}
                        selected={
                          location.pathname === "/akun-tendik" ||
                          location.pathname === "/akun-cs-tabungan"
                        }
                      >
                        <StyledListItemIcon>
                          <ManageAccounts />
                        </StyledListItemIcon>
                        <ListItemText primary="Tendik" />
                      </StyledListItemButton>
                    )}
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/siswa-list")}
                      selected={location.pathname === "/siswa-list"}
                    >
                      <StyledListItemIcon>
                        <School />
                      </StyledListItemIcon>
                      <ListItemText primary="Siswa" />
                    </StyledListItemButton>
                  </List>
                </Collapse>
              </>
            )}

          {/* Buku Tabungan guru/karyawan sementara di-hide; hanya tampil di login wali/ortu.
          {!hasRole("BENDAHARA") &&
            !hasRole("ADMIN") &&
            !hasRole("KEPSEK") && (
              <StyledListItemButton
                sx={{ order: 100 }}
                onClick={() => navigate("/buku-tabungan")}
                selected={location.pathname === "/buku-tabungan"}
              >
                <StyledListItemIcon>
                  <Savings />
                </StyledListItemIcon>
                <ListItemText primary="Buku Tabungan" />
              </StyledListItemButton>
            )}
          */}

          {/* BENDAHARA Specific Menu */}
          {(hasRole("BENDAHARA") || hasRole("ADMIN") || hasRole("KEPSEK")) && (
            <>
              {!hasRole("KEPSEK") && (
                <StyledListItemButton
                  onClick={() => navigate("/bendahara/spp")}
                  selected={location.pathname === "/bendahara/spp"}
                >
                  <StyledListItemIcon>
                    <MeetingRoom />
                  </StyledListItemIcon>
                  <ListItemText primary="Status SPP bulanan Siswa" />
                </StyledListItemButton>
              )}
              {/* Buku Tabungan guru/karyawan sementara di-hide; hanya tampil di login wali/ortu.
              <StyledListItemButton
                sx={{ order: 100 }}
                onClick={() => navigate("/buku-tabungan")}
                selected={location.pathname === "/buku-tabungan"}
              >
                <StyledListItemIcon>
                  <Savings />
                </StyledListItemIcon>
                <ListItemText primary="Buku Tabungan" />
              </StyledListItemButton>
              */}
              <StyledListItemButton
                sx={{ order: 101 }}
                onClick={() => setOpenTabungan(!openTabungan)}
                selected={location.pathname.startsWith("/bendahara/tabungan")}
              >
                <StyledListItemIcon>
                  <Savings />
                </StyledListItemIcon>
                <ListItemText primary="Tabungan" />
                {openTabungan ? <ExpandLess /> : <ExpandMore />}
              </StyledListItemButton>
              <Collapse
                in={openTabungan}
                timeout="auto"
                unmountOnExit
                sx={{ order: 101 }}
              >
                <List component="div" disablePadding>
                  <StyledListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => navigate("/bendahara/tabungan")}
                    selected={location.pathname === "/bendahara/tabungan"}
                  >
                    <StyledListItemIcon>
                      <EditNote />
                    </StyledListItemIcon>
                    <ListItemText primary="Input Tabungan" />
                  </StyledListItemButton>
                  <StyledListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => navigate("/bendahara/tabungan/rekap-bulanan")}
                    selected={
                      location.pathname === "/bendahara/tabungan/rekap-bulanan"
                    }
                  >
                    <StyledListItemIcon>
                      <ListAlt />
                    </StyledListItemIcon>
                    <ListItemText primary="Rekapitulasi Bulanan" />
                  </StyledListItemButton>
                  {!(isMiddleOrHigh && isReligious) && (
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/bendahara/tabungan/rekap-harian")}
                      selected={
                        location.pathname === "/bendahara/tabungan/rekap-harian"
                      }
                    >
                      <StyledListItemIcon>
                        <EventAvailable />
                      </StyledListItemIcon>
                      <ListItemText primary="Rekapitulasi Harian" />
                    </StyledListItemButton>
                  )}
                  {isMiddleOrHigh && isReligious && (
                    <>
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate("/bendahara/tabungan/rekap-laundry")}
                        selected={
                          location.pathname === "/bendahara/tabungan/rekap-laundry"
                        }
                      >
                        <StyledListItemIcon>
                          <ListAlt />
                        </StyledListItemIcon>
                        <ListItemText primary="Rekapitulasi Laundry" />
                      </StyledListItemButton>
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate("/bendahara/tabungan/rekap-jajan")}
                        selected={
                          location.pathname === "/bendahara/tabungan/rekap-jajan"
                        }
                      >
                        <StyledListItemIcon>
                          <ListAlt />
                        </StyledListItemIcon>
                        <ListItemText primary="Rekapitulasi Jajan" />
                      </StyledListItemButton>
                    </>
                  )}
                  <StyledListItemButton
                    sx={{ pl: 4 }}
                    onClick={() =>
                      navigate("/bendahara/tabungan/rekap-tahunan")
                    }
                    selected={
                      location.pathname === "/bendahara/tabungan/rekap-tahunan"
                    }
                  >
                    <StyledListItemIcon>
                      <DateRange />
                    </StyledListItemIcon>
                    <ListItemText primary="Rekapitulasi Tahunan" />
                  </StyledListItemButton>
                  {!(isMiddleOrHigh && isReligious) && (
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/bendahara/tabungan/setoran-bank")}
                      selected={
                        location.pathname === "/bendahara/tabungan/setoran-bank"
                      }
                    >
                      <StyledListItemIcon>
                        <AccountBalance />
                      </StyledListItemIcon>
                      <ListItemText primary="Setoran Bank" />
                    </StyledListItemButton>
                  )}
                  {!(isMiddleOrHigh && isReligious) && (
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/bendahara/tabungan/laporan-anak")}
                      selected={
                        location.pathname === "/bendahara/tabungan/laporan-anak"
                      }
                    >
                      <StyledListItemIcon>
                        <AssignmentTurnedIn />
                      </StyledListItemIcon>
                      <ListItemText primary="Laporan Tabungan Anak" />
                    </StyledListItemButton>
                  )}
                  {!(isMiddleOrHigh && isReligious) && (
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/bendahara/tabungan/alumni")}
                      selected={location.pathname === "/bendahara/tabungan/alumni"}
                    >
                      <StyledListItemIcon>
                        <School />
                      </StyledListItemIcon>
                      <ListItemText primary="Tabungan Alumni" />
                    </StyledListItemButton>
                  )}
                </List>
              </Collapse>
            </>
          )}

          {!hasRole("BENDAHARA") && (
            <>
              {/* Monitor Raport for WAKUR, TATAUSAHA, KEPSEK, ADMIN, and YAYASAN */}
              {(hasRole("WAKUR") || hasRole("TATAUSAHA") || hasRole("KEPSEK") || hasRole("ADMIN") || hasRole("YAYASAN")) && (
                <>
                  <StyledListItemButton
                    onClick={() => setOpenMonitorRaport(!openMonitorRaport)}
                  >
                    <StyledListItemIcon>
                      <Description />
                    </StyledListItemIcon>
                    <ListItemText primary="Monitor Raport" />
                    {openMonitorRaport ? <ExpandLess /> : <ExpandMore />}
                  </StyledListItemButton>

                  <Collapse in={openMonitorRaport} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {!((hasRole("WALIKELAS") && isEarlyChildhood) || isPaudMonitorLimited || hasRole("TATAUSAHA")) && (
                        <StyledListItemButton
                          sx={{ pl: 6 }}
                          onClick={() =>
                            navigate("/monitor-raport/daftar-pengisian")
                          }
                          selected={
                            location.pathname ===
                            "/monitor-raport/daftar-pengisian"
                          }
                        >
                          <StyledListItemIcon>
                            <EditNote />
                          </StyledListItemIcon>
                          <ListItemText
                            primary="Daftar Pengisian"
                            primaryTypographyProps={{ fontSize: "0.95rem" }}
                          />
                        </StyledListItemButton>
                      )}

                      <StyledListItemButton
                        sx={{ pl: 6 }}
                        onClick={() => navigate("/monitor-raport/hasil-raport")}
                        selected={
                          location.pathname === "/monitor-raport/hasil-raport"
                        }
                      >
                        <StyledListItemIcon>
                          <Description />
                        </StyledListItemIcon>
                        <ListItemText
                          primary="Hasil Raport 1 Sem"
                          primaryTypographyProps={{ fontSize: "0.95rem" }}
                        />
                      </StyledListItemButton>

                      {!((hasRole("WALIKELAS") && isEarlyChildhood) || isPaudMonitorLimited) && (
                        <>
                          <StyledListItemButton
                            sx={{ pl: 6 }}
                            onClick={() =>
                              navigate("/monitor-raport/hasil-raport-tengah")
                            }
                            selected={
                              location.pathname ===
                              "/monitor-raport/hasil-raport-tengah"
                            }
                          >
                            <StyledListItemIcon>
                              <Description />
                            </StyledListItemIcon>
                            <ListItemText
                              primary="Hasil Raport Tengah Sem"
                              primaryTypographyProps={{ fontSize: "0.95rem" }}
                            />
                          </StyledListItemButton>

                          <StyledListItemButton
                            sx={{ pl: 6 }}
                            onClick={() =>
                              navigateWithRefresh("/monitor-raport/rekap-nilai")
                            }
                            selected={
                              location.pathname === "/monitor-raport/rekap-nilai"
                            }
                          >
                            <StyledListItemIcon>
                              <ListAlt />
                            </StyledListItemIcon>
                            <ListItemText
                              primary="Rekap Nilai"
                              primaryTypographyProps={{ fontSize: "0.95rem" }}
                            />
                          </StyledListItemButton>

                          <StyledListItemButton
                            sx={{ pl: 6 }}
                            onClick={() =>
                              navigateWithRefresh(
                                "/monitor-raport/rekap-nilai-tengah",
                              )
                            }
                            selected={
                              location.pathname ===
                              "/monitor-raport/rekap-nilai-tengah"
                            }
                          >
                            <StyledListItemIcon>
                              <ListAlt />
                            </StyledListItemIcon>
                            <ListItemText
                              primary="Rekap Nilai Tengah Sem"
                              primaryTypographyProps={{ fontSize: "0.95rem" }}
                            />
                          </StyledListItemButton>

                          {/* Raport Pondok Pesantren (Only if Religious) */}
                          {isReligious && !isPaudMonitorLimited && (
                            <StyledListItemButton
                              sx={{ pl: 6 }}
                              onClick={() =>
                                navigate("/monitor-raport/raport-pondok")
                              }
                              selected={
                                location.pathname ===
                                "/monitor-raport/raport-pondok"
                              }
                            >
                              <StyledListItemIcon>
                                <Mosque />
                              </StyledListItemIcon>
                              <ListItemText
                                primary="Raport Pondok Pesantren"
                                primaryTypographyProps={{ fontSize: "0.95rem" }}
                              />
                            </StyledListItemButton>
                          )}
                        </>
                      )}
                    </List>
                  </Collapse>
                </>
              )}

              {/* Pengisian Raport */}
              {!hasRole("TATAUSAHA") &&
                !hasRole("WALIKELAS") &&
                !hasRole("WAKUR") &&
                !hasRole("KEPSEK") &&
                !hasRole("YAYASAN") && (
                  <StyledListItemButton
                    onClick={() => navigate("/mapel-detail")}
                    selected={location.pathname === "/mapel-detail"}
                  >
                    <StyledListItemIcon>
                      <EditNote />
                    </StyledListItemIcon>
                    <ListItemText primary="Pengisian Raport" />
                  </StyledListItemButton>
                )}

              {/* Wali Kelas Section */}
              {hasRole("WALIKELAS") && (
                <>
                  <StyledListItemButton
                    onClick={() => navigate("/pilih-siswa")}
                    selected={location.pathname === "/pilih-siswa"}
                  >
                    <StyledListItemIcon>
                      <Class />
                    </StyledListItemIcon>
                    <ListItemText primary="Siswa Kelas" />
                  </StyledListItemButton>

                  <StyledListItemButton
                    onClick={() => setOpenAbsensi(!openAbsensi)}
                  >
                    <StyledListItemIcon>
                      <EventAvailable />
                    </StyledListItemIcon>
                    <ListItemText primary="Catatan Murid" />
                    {openAbsensi ? <ExpandLess /> : <ExpandMore />}
                  </StyledListItemButton>
                  <Collapse in={openAbsensi} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                    >
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate("/absensi")}
                        selected={location.pathname === "/absensi"}
                      >
                        <StyledListItemIcon>
                          <AssignmentTurnedIn />
                        </StyledListItemIcon>
                        <ListItemText primary="Absensi Siswa" />
                      </StyledListItemButton>
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigate(isEarlyChildhood ? "/aspek" : "/ko-kurikuler")}
                        selected={
                          isEarlyChildhood
                            ? location.pathname === "/aspek"
                            : location.pathname === "/ko-kurikuler"
                        }
                      >
                        <StyledListItemIcon>
                          <ListAlt />
                        </StyledListItemIcon>
                        <ListItemText primary={isEarlyChildhood ? "Aspek" : kokurikulerMenuLabel} />
                      </StyledListItemButton>
                      {isEarlyChildhood && (
                        <StyledListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => navigate("/ko-kurikuler-paud")}
                          selected={location.pathname === "/ko-kurikuler-paud"}
                        >
                          <StyledListItemIcon>
                            <ListAlt />
                          </StyledListItemIcon>
                          <ListItemText primary="Kokurikuler (Dimensi)" />
                        </StyledListItemButton>
                      )}
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() =>
                          navigate(
                            isEarlyChildhood
                              ? "/satuan-paud-sejenis"
                              : "/ekstrakulikuler",
                          )
                        }
                        selected={
                          isEarlyChildhood
                            ? location.pathname === "/satuan-paud-sejenis"
                            : location.pathname === "/ekstrakulikuler"
                        }
                      >
                        <StyledListItemIcon>
                          <Palette />
                        </StyledListItemIcon>
                        <ListItemText
                          primary={
                            isEarlyChildhood
                              ? "Satuan Paud Sejenis"
                              : "Ekstrakulikuler"
                          }
                        />
                      </StyledListItemButton>
                      {isEarlyChildhood && (
                        <StyledListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => navigate("/capaian-perkembangan-paud")}
                          selected={location.pathname === "/capaian-perkembangan-paud"}
                        >
                          <StyledListItemIcon>
                            <MenuBook />
                          </StyledListItemIcon>
                          <ListItemText primary="Capaian Perkembangan" />
                        </StyledListItemButton>
                      )}
                      {isEarlyChildhood && (
                        <StyledListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => navigate("/capaian-bidang-pengembangan-paud")}
                          selected={location.pathname === "/capaian-bidang-pengembangan-paud"}
                        >
                          <StyledListItemIcon>
                            <AccountTree />
                          </StyledListItemIcon>
                          <ListItemText primary="Pengembangan Al Islam" />
                        </StyledListItemButton>
                      )}
                    </List>
                  </Collapse>

                  <StyledListItemButton
                    onClick={() => navigate("/catatan-wali")}
                    selected={location.pathname === "/catatan-wali"}
                  >
                    <StyledListItemIcon>
                      <MenuBook />
                    </StyledListItemIcon>
                    <ListItemText
                      primary={isEarlyChildhood ? "Refleksi Guru" : "Catatan Guru"}
                    />
                  </StyledListItemButton>

                  {isEarlyChildhood && (
                    <StyledListItemButton
                      onClick={() => navigate("/foto-rapor-walas")}
                      selected={location.pathname === "/foto-rapor-walas"}
                    >
                      <StyledListItemIcon>
                        <EditNote />
                      </StyledListItemIcon>
                      <ListItemText primary="Foto Rapor Semester 1" />
                    </StyledListItemButton>
                  )}

                  {/* Sikap Menu - Only for Middle/High School */}
                  {isMiddleOrHigh && (
                    <>
                      <StyledListItemButton
                        onClick={() => setOpenSikap(!openSikap)}
                      >
                        <StyledListItemIcon>
                          <EmojiPeople />
                        </StyledListItemIcon>
                        <ListItemText primary="Sikap" />
                        {openSikap ? <ExpandLess /> : <ExpandMore />}
                      </StyledListItemButton>
                      <Collapse in={openSikap} timeout="auto" unmountOnExit>
                        <List
                          component="div"
                          disablePadding
                          sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                        >
                          <StyledListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => navigate("/sikap-spiritual")}
                            selected={location.pathname === "/sikap-spiritual"}
                          >
                            <StyledListItemIcon>
                              <VolunteerActivism />
                            </StyledListItemIcon>
                            <ListItemText primary="Spiritual" />
                          </StyledListItemButton>
                          <StyledListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => navigate("/sikap-sosial")}
                            selected={location.pathname === "/sikap-sosial"}
                          >
                            <StyledListItemIcon>
                              <Groups />
                            </StyledListItemIcon>
                            <ListItemText primary="Sosial" />
                          </StyledListItemButton>
                        </List>
                      </Collapse>
                    </>
                  )}
                </>
              )}

          {/* Rapor Siswa Dropdown */}
          {!hasRole("TATAUSAHA") &&
            !hasRole("WAKUR") &&
            !hasRole("GURU") &&
            !hasRole("KEPSEK") &&
            !hasRole("YAYASAN") && (
              <>
                <StyledListItemButton onClick={() => setOpenRapor(!openRapor)}>
                <StyledListItemIcon>
                  <Book />
                </StyledListItemIcon>
                <ListItemText primary="Rapor Siswa" />
                {openRapor ? <ExpandLess /> : <ExpandMore />}
              </StyledListItemButton>
              <Collapse in={openRapor} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                >
                  {/* {!isEarlyChildhood && (
                    <StyledListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => navigate("/hasil-raport-tengah")}
                      selected={location.pathname === "/hasil-raport-tengah"}
                    >
                      <StyledListItemIcon>
                        <Description />
                      </StyledListItemIcon>
                      <ListItemText primary="Hasil Raport Tengah Sem" />
                    </StyledListItemButton>
                  )} */}
                  <StyledListItemButton
                    sx={{ pl: 4 }}
                    onClick={() =>
                      navigate(isEarlyChildhood ? "/hasil-raport-paud" : "/hasil-raport")
                    }
                    selected={
                      location.pathname === "/hasil-raport" ||
                      location.pathname === "/hasil-raport-paud"
                    }
                  >
                    <StyledListItemIcon>
                      <Description />
                    </StyledListItemIcon>
                    <ListItemText
                      primary={
                        isEarlyChildhood
                          ? "Hasil Rapor 1 Sem PAUD"
                          : "Hasil Raport 1 Sem"
                      }
                    />
                  </StyledListItemButton>
                 {!isEarlyChildhood && (
                  <StyledListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => navigate("/raport-tengah-semester")}
                    selected={location.pathname === "/raport-tengah-semester"}
                  >
                    <StyledListItemIcon>
                      <NoteAlt />
                    </StyledListItemIcon>
                    <ListItemText primary="Rapor Tengah Sem" />
                  </StyledListItemButton>
                 )}

                 
                  {!(hasRole("WALIKELAS") && isEarlyChildhood) && (
                    <>
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() => navigateWithRefresh("/rekap-nilai")}
                        selected={location.pathname === "/rekap-nilai"}
                      >
                        <StyledListItemIcon>
                          <TrendingUp />
                        </StyledListItemIcon>
                        <ListItemText primary="Rekap Nilai" />
                      </StyledListItemButton>
                      <StyledListItemButton
                        sx={{ pl: 4 }}
                        onClick={() =>
                          navigateWithRefresh("/rekap-tengah-semester")
                        }
                        selected={
                          location.pathname === "/rekap-tengah-semester"
                        }
                      >
                        <StyledListItemIcon>
                          <TrendingUp />
                        </StyledListItemIcon>
                        <ListItemText primary="Rekap Tengah Sem" />
                      </StyledListItemButton>
                      
                      {isReligious && (
                        <StyledListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => navigate("/raport-pondok")}
                          selected={location.pathname === "/raport-pondok"}
                        >
                          <StyledListItemIcon>
                            <Mosque />
                          </StyledListItemIcon>
                          <ListItemText primary="Raport Pondok Pesantren" />
                        </StyledListItemButton>
                      )}
                    </>
                  )}
                </List>
              </Collapse>
            </>
          )}

          {/* Data Mapel dan Kelas */}
          {!hasRole("TATAUSAHA") &&
            !hasRole("GURU") &&
            !hasRole("WALIKELAS") &&
            !hasRole("KEPSEK") &&
            !hasRole("YAYASAN") && (
              <>
                {!isWakurPaud && (
                  <StyledListItemButton
                    onClick={() => {
                      notify.warning(
                        "Pastikan urutan data mata pelajaran sudah sesuai dengan urutan di rapor.",
                        { color: "red" }
                      );
                      navigate("/mapel");
                    }}
                    selected={location.pathname === "/mapel"}
                  >
                    <StyledListItemIcon>
                      <MenuBook />
                    </StyledListItemIcon>
                    <ListItemText primary="Data Mapel" />
                  </StyledListItemButton>
                )}

                    {/* Master Acuan menu logic */}
                    {hasRole("WAKUR") && (
                      <>
                        <StyledListItemButton
                          onClick={() => setOpenAcuan?.((v) => !v)}
                        >
                          <StyledListItemIcon>
                            <MenuBook />
                          </StyledListItemIcon>
                          <ListItemText primary="ACUAN" />
                          {openAcuan ? <ExpandLess /> : <ExpandMore />}
                        </StyledListItemButton>
                        <Collapse in={openAcuan} timeout="auto" unmountOnExit>
                          <List
                            component="div"
                            disablePadding
                            sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                          >
                            {isEarlyChildhood ? (
                              <>
                                
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-paud")}
                                  selected={location.pathname === "/acuan-paud"}
                                >
                                  <StyledListItemIcon>
                                    <ListAlt />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Aspek" />
                                </StyledListItemButton>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-kokurikuler-paud")}
                                  selected={location.pathname === "/acuan-kokurikuler-paud"}
                                >
                                  <StyledListItemIcon>
                                    <ListAlt />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Kokurikuler (Dimensi)" />
                                </StyledListItemButton>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-ekstrakurikuler-paud")}
                                  selected={location.pathname === "/acuan-ekstrakurikuler-paud"}
                                >
                                  <StyledListItemIcon>
                                    <Palette />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Satuan Paud Sejenis" />
                                </StyledListItemButton>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-capaian-paud")}
                                  selected={location.pathname === "/acuan-capaian-paud"}
                                >
                                  <StyledListItemIcon>
                                    <MenuBook />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Capaian Perkembangan" />
                                </StyledListItemButton>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-bidang-pengembangan-paud")}
                                  selected={location.pathname === "/acuan-bidang-pengembangan-paud"}
                                >
                                  <StyledListItemIcon>
                                    <AccountTree />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Pengembangan Al Islam" />
                                </StyledListItemButton>
                              </>
                            ) : (
                              <>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-kokurikuler")}
                                  selected={location.pathname === "/acuan-kokurikuler"}
                                >
                                  <StyledListItemIcon>
                                    <ListAlt />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Kokurikuler" />
                                </StyledListItemButton>
                                <StyledListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() => navigate("/acuan-ekstrakurikuler")}
                                  selected={
                                    location.pathname === "/acuan-ekstrakurikuler"
                                  }
                                >
                                  <StyledListItemIcon>
                                    <Palette />
                                  </StyledListItemIcon>
                                  <ListItemText primary="Acuan Ekstrakurikuler" />
                                </StyledListItemButton>
                              </>
                            )}
                          </List>
                        </Collapse>
                      </>
                    )}

                    {/* Dropdown Kelas (Guru & Walas) */}
                    <StyledListItemButton
                      onClick={() => setOpenKelasWakur(!openKelasWakur)}
                    >
                      <StyledListItemIcon>
                        <DoorBack />
                      </StyledListItemIcon>
                      <ListItemText primary="Kelas" />
                      {openKelasWakur ? <ExpandLess /> : <ExpandMore />}
                    </StyledListItemButton>
                    <Collapse in={openKelasWakur} timeout="auto" unmountOnExit>
                      <List
                        component="div"
                        disablePadding
                        sx={{ bgcolor: "rgba(0, 0, 0, 0.15)" }}
                      >
                        {!isWakurPaud && (
                          <StyledListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => navigate("/kelas-wakur-guru")}
                            selected={location.pathname === "/kelas-wakur-guru"}
                          >
                            <StyledListItemIcon>
                              <CoPresent />
                            </StyledListItemIcon>
                            <ListItemText primary="Guru" />
                          </StyledListItemButton>
                        )}

                        <StyledListItemButton
                          sx={{ pl: 4 }}
                          onClick={() => navigate("/kelas-wakur-walas")}
                          selected={location.pathname === "/kelas-wakur-walas"}
                        >
                          <StyledListItemIcon>
                            <Person />
                          </StyledListItemIcon>
                          <ListItemText primary="Wali Kelas" />
                        </StyledListItemButton>
                      </List>
                    </Collapse>
                  </>
                )}

              {hasRole("ADMIN") && (
                <StyledListItemButton
                  onClick={() => navigate("/user-tu")}
                  selected={location.pathname === "/user-tu"}
                >
                  <StyledListItemIcon>
                    <ManageAccounts />
                  </StyledListItemIcon>
                  <ListItemText primary="Manajemen Pengguna" />
                </StyledListItemButton>
              )}
            </>
          )}
        </List>

        {isCsTabungan && (
          <List component="nav">
            <StyledListItemButton
              onClick={() => navigate("/profile")}
              selected={location.pathname === "/profile"}
            >
              <StyledListItemIcon>
                <Person />
              </StyledListItemIcon>
              <ListItemText primary="Profil" />
            </StyledListItemButton>
            <StyledListItemButton
              onClick={() => navigate("/dashboard")}
              selected={location.pathname === "/dashboard"}
            >
              <StyledListItemIcon>
                <Dashboard />
              </StyledListItemIcon>
              <ListItemText primary="Beranda" />
            </StyledListItemButton>
            <StyledListItemButton
              onClick={() => navigate("/create-identitas")}
              selected={location.pathname === "/create-identitas"}
            >
              <StyledListItemIcon>
                <School />
              </StyledListItemIcon>
              <ListItemText primary="Identitas Sekolah" />
            </StyledListItemButton>
            <StyledListItemButton
              onClick={() => navigate("/strucktur-organisasi")}
              selected={location.pathname === "/strucktur-organisasi"}
            >
              <StyledListItemIcon>
                <AccountTree />
              </StyledListItemIcon>
              <ListItemText primary="Struktur Organisasi" />
            </StyledListItemButton>
            {/* Buku Tabungan guru/karyawan sementara di-hide; hanya tampil di login wali/ortu.
            <StyledListItemButton
              onClick={() => navigate("/buku-tabungan")}
              selected={location.pathname === "/buku-tabungan"}
            >
              <StyledListItemIcon>
                <Savings />
              </StyledListItemIcon>
              <ListItemText primary="Buku Tabungan" />
            </StyledListItemButton>
            */}
          </List>
        )}

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)", my: 1 }} />

        <List>
          <StyledListItemButton
            onClick={handleLogout}
            sx={{
              color: "#ffcdd2",
              "&:hover": {
                bgcolor: "rgba(244, 67, 54, 0.15)",
                color: "#ffcdd2",
              },
            }}
          >
            <StyledListItemIcon>
              <Logout sx={{ color: "#ef5350" }} />
            </StyledListItemIcon>
            <ListItemText primary="Keluar" />
          </StyledListItemButton>
        </List>

        <Box
          sx={{
            px: 2.5,
            pb: 2,
            textAlign: "center",
            color: "rgba(255, 255, 255, 0.72)",
            fontSize: "0.78rem",
            letterSpacing: "0.02em",
          }}
        >
          Versi aplikasi v{appVersion}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: desktopOpen ? { sm: drawerWidth } : 0,
        flexShrink: { sm: 0 },
        transition: "width 0.3s",
      }}
      aria-label="mailbox folders"
    >
      {/* Mobile Drawer */}
      <SwipeableDrawer
        variant="temporary"
        open={mobileOpen}
        onOpen={handleDrawerToggle}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            borderRight: "none",
            borderTopRightRadius: "16px",
            borderBottomRightRadius: "16px",
          },
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
      {/* Desktop Drawer */}
      {desktopOpen && (
      <Drawer
        variant="permanent"
        sx={{
          display: desktopOpen ? { xs: "none", sm: "block", md:"block" } : "none",
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            borderRight: "none",
          },
        }}
        open={desktopOpen}
      >
        {drawerContent}
      </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
